import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import type { SequenceStep } from "@/lib/outreach-sequences";

function classifyTask(title: string): string {
  const t = title.toLowerCase();
  if (t.includes("email")) return "Email";
  if (t.includes("call") || t.includes("check-in")) return "Call";
  if (t.includes("visit") || t.includes("tour") || t.includes("lunch") || t.includes("meeting") || t.includes("drop off")) return "Visit";
  return "Other";
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  steps: SequenceStep[];
  sourceType: string;
  onConfirm: () => void;
}

export default function SequencePreviewDialog({ open, onOpenChange, steps, sourceType, onConfirm }: Props) {
  const today = new Date();
  const typeCounts: Record<string, number> = {};
  steps.forEach(s => {
    const type = classifyTask(s.title);
    typeCounts[type] = (typeCounts[type] || 0) + 1;
  });
  const summary = Object.entries(typeCounts).map(([t, c]) => `${c} ${t.toLowerCase()}${c > 1 ? "s" : ""}`).join(", ");

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>Launch Outreach Sequence</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="font-medium text-foreground">{sourceType}</span> — {steps.length} tasks: {summary}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {steps.map(s => {
            const dueDate = new Date(today.getTime() + s.dayOffset * 86400000);
            const type = classifyTask(s.title);
            return (
              <div key={s.step} className="flex items-start gap-3 rounded-lg border p-3 text-sm">
                <Badge variant="outline" className="shrink-0 mt-0.5">Step {s.step}</Badge>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-foreground">{s.title}</div>
                  <div className="text-muted-foreground text-xs mt-0.5">{s.description}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Due: {format(dueDate, "MMM d, yyyy")} · <span className="capitalize">{type}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Launch Sequence</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
